import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import { Reveal } from "../components/Reveal";
import { CtaBanner } from "../components/CtaBanner";
import { CompareSlider } from "../components/BeforeAfter";
import { BEFORE_AFTER } from "../data/content";

const WORK_PHOTOS = [
    { src: "/leistung-heckenschnitt.webp", alt: "Heckenschnitt im Landkreis Celle – Formschnitt mit der Heckenschere", label: "Heckenschnitt" },
    { src: "/leistung-rasenpflege.webp", alt: "Rasenpflege im Landkreis Celle – Mähen mit dem Aufsitzmäher", label: "Rasenpflege" },
    { src: "/leistung-laubentfernung.webp", alt: "Laubentfernung im Landkreis Celle – Arbeitseinsatz mit dem Laubbläser", label: "Laubentfernung" },
];

export const Referenzen = () => (
    <PageLayout>
        <section className="pt-28 lg:pt-40 pb-14 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
            <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-leaf font-semibold mb-4">
                    Referenzen · Landkreis Celle
                </p>
                <h1 className="font-display font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-3xl">
                    Ergebnisse statt Versprechen.
                </h1>
                <p className="mt-6 text-base sm:text-lg text-shade leading-relaxed max-w-2xl">
                    Worte überzeugen wenig – Ergebnisse schon. Hier sehen Sie echte Projekte aus dem
                    Landkreis Celle, vor und nach meiner Arbeit. Diese Seite wächst mit jedem neuen Projekt.
                </p>
            </Reveal>
        </section>

        {BEFORE_AFTER.map((pair, i) => (
            <section key={pair.id} className="relative bg-forest-deep grain py-14 lg:py-20 overflow-hidden mb-1 last:mb-0">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-leaf-soft mb-3">
                            Projekt {String(i + 1).padStart(2, "0")}
                        </p>
                        <h2 className="font-display font-semibold tracking-tight text-white text-2xl sm:text-3xl">
                            {pair.title}
                        </h2>
                        <p className="mt-3 text-base text-white/60 max-w-xl leading-relaxed">{pair.caption}</p>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-7">
                        <CompareSlider pair={pair} />
                    </Reveal>
                </div>
            </section>
        ))}

        <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
            <Reveal>
                <h2 className="font-display font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
                    Bei der Arbeit
                </h2>
                <p className="mt-4 text-base sm:text-lg text-shade max-w-2xl leading-relaxed">
                    Einblicke in die tägliche Arbeit – mit professioneller Technik und dem Anspruch,
                    jedes Grundstück sauber zu verlassen.
                </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {WORK_PHOTOS.map((p, i) => (
                    <Reveal key={p.src} delay={i * 0.08}>
                        <figure className="group">
                            <img
                                src={p.src}
                                alt={p.alt}
                                loading="lazy"
                                className="w-full aspect-[4/3] object-cover rounded-3xl transition-transform duration-500 group-hover:scale-[1.02]"
                            />
                            <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.22em] text-fog">
                                {p.label}
                            </figcaption>
                        </figure>
                    </Reveal>
                ))}
            </div>
            <Reveal className="mt-12">
                <div className="flex flex-wrap gap-2.5">
                    {[
                        { to: "/heckenschnitt-celle", label: "Mehr zum Heckenschnitt" },
                        { to: "/hochdruckreinigung-celle", label: "Mehr zur Pflasterreinigung" },
                        { to: "/rasenpflege-celle", label: "Mehr zur Rasenpflege" },
                    ].map((r) => (
                        <Link
                            key={r.to}
                            to={r.to}
                            data-testid={`referenzen-link-${r.to.slice(1)}`}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-hairline text-sm font-medium text-forest transition-colors duration-200 hover:bg-leaf-light hover:border-leaf/30"
                        >
                            {r.label}
                            <ArrowRight size={13} />
                        </Link>
                    ))}
                </div>
            </Reveal>
        </section>

        <CtaBanner title="Ihr Projekt könnte das nächste sein." />
    </PageLayout>
);
